// components/AdditionalInfoSection.tsx
// 「ランク」「備考（キーワード・アシスト入力）」「拡張フィールド」を一つにまとめた再利用コンポーネント。
// AdditionalInfoPage 相当の機能を、親 Form を共有する形で他ページ（EditResultPage の本登録モード等）にも組み込めるようにする。
//
// アシスト入力の初期化・描画・挿入は useAssistInput フックに集約済み（FollowFormModal 等と共通ロジック）。

import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Input, Select, Button, Typography, Space, Checkbox } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import type { TextAreaRef } from 'antd/lib/input/TextArea';
import { TagOutlined, RobotOutlined } from '@ant-design/icons';
import { additionalAPI } from '../services/additional-api';
import { optionFieldAPI, OptionFieldListResponse } from '../services/option-field-api';
import OptionFieldGroup from './OptionFields/OptionFieldGroup';
import KeywordSelectModal from './KeywordSelectModal';
import AssistInputModal from './AssistInputModal';
import { useAssistInput } from '../hooks/useAssistInput';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface RankOption {
  value: number | string;
  label: string;
}

export interface AdditionalInfoSectionProps {
  form: FormInstance;
  /** ランクフィールド名（デフォルト: 'rankno'） */
  rankFieldName?: string;
  /** 備考フィールド名（デフォルト: 'notes'） */
  remarksFieldName?: string;
  /** 拡張フィールドの値 */
  optionFieldValues: Record<string, any>;
  /** 拡張フィールド値変更コールバック */
  onOptionFieldValuesChange: (values: Record<string, any>) => void;

  // ===== 備考の新規 / 既存チェックボックス =====
  /** 既存の備考（仮登録の本登録で重複顧客があるときに渡す）。指定時のみチェックボックス表示 */
  existingRemarks?: string;
  /** 「新規」側として表示する備考（仮登録時の備考）。指定時のみチェックボックス表示 */
  newRemarks?: string;
  /** 新規側を反映するか（チェックON） */
  remarksNewChecked?: boolean;
  /** 既存側を反映するか（チェックON） */
  remarksExistingChecked?: boolean;
  /** 新規側チェック変更コールバック */
  onRemarksNewCheckedChange?: (checked: boolean) => void;
  /** 既存側チェック変更コールバック */
  onRemarksExistingCheckedChange?: (checked: boolean) => void;

  // ===== 「顧客備考」欄の重複整備（doMergeRemarks） =====
  /** 「顧客備考」欄の重複整備チェックボックスを表示するか */
  showMergeRemarks?: boolean;
  /** 「顧客備考」欄の重複整備を行うか（チェックON） */
  mergeRemarksChecked?: boolean;
  /** 重複整備チェック変更コールバック */
  onMergeRemarksChange?: (checked: boolean) => void;
}

const AdditionalInfoSection: React.FC<AdditionalInfoSectionProps> = ({
  form,
  rankFieldName = 'rankno',
  remarksFieldName = 'notes',
  optionFieldValues,
  onOptionFieldValuesChange,
  existingRemarks,
  newRemarks,
  remarksNewChecked = true,
  remarksExistingChecked = true,
  onRemarksNewCheckedChange,
  onRemarksExistingCheckedChange,
  showMergeRemarks = false,
  mergeRemarksChecked = true,
  onMergeRemarksChange,
}) => {
  // ランク
  const [rankOptions, setRankOptions] = useState<RankOption[]>([]);

  // 拡張フィールド
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);

  // キーワード入力モーダル
  const [keywordModalVisible, setKeywordModalVisible] = useState<boolean>(false);
  const [keywordOptions, setKeywordOptions] = useState<any[]>([]);
  const [keywordLoadError, setKeywordLoadError] = useState<boolean>(false);

  // 備考の参照（カーソル位置取得用）
  const textAreaRef = useRef<TextAreaRef>(null);

  // ====== アシスト入力（共通フック） ======
  // 選択確定時に備考フィールドのカーソル位置へ挿入する
  const insertAssistText = (text: string) => {
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    const currentValue: string = form.getFieldValue(remarksFieldName) || '';
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
      form.setFieldsValue({ [remarksFieldName]: newValue });
      setTimeout(() => {
        textarea.focus();
        const pos = start + text.length;
        textarea.setSelectionRange(pos, pos);
      }, 10);
    } else {
      const newValue = currentValue + (currentValue ? ' ' : '') + text;
      form.setFieldsValue({ [remarksFieldName]: newValue });
    }
  };

  const assist = useAssistInput({
    kind: 'customerRemarks',
    hiddenInputId: 'additional_info_section_customer_remarks',
    defaultUserField: 'defaultcustomerassistno',
    onInsert: insertAssistText,
  });

  // ====== 初期データ取得（ランク／キーワード／拡張フィールド） ======
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ランク
        const rankResult = await additionalAPI.getRankList();
        if (!cancelled && rankResult.success && rankResult.data) {
          setRankOptions(
            rankResult.data.map((r: any) => ({ value: r.id, label: r.name }))
          );
        }
      } catch (e) {
        console.error('ランク取得エラー:', e);
      }

      try {
        // キーワード（備考欄用）。レスポンスは文字列の場合 / { keywordlist } / { result } の3形式あり
        const keywordResult = await additionalAPI.getKeywordList('01');
        if (!cancelled) {
          if (keywordResult.success && keywordResult.data) {
            try {
              let keywordData: any = keywordResult.data;
              if (typeof keywordData === 'string') {
                keywordData = JSON.parse(keywordData);
              }
              if (keywordData?.keywordlist && Array.isArray(keywordData.keywordlist)) {
                setKeywordOptions(keywordData.keywordlist);
                setKeywordLoadError(false);
              } else if (keywordData?.result && Array.isArray(keywordData.result)) {
                const keywordList = keywordData.result.map((item: any) => ({
                  keyword: item.keywordName || '',
                  keywordcontent: item.keywordContent || item.keywordName || '',
                }));
                setKeywordOptions(keywordList);
                setKeywordLoadError(false);
              } else {
                setKeywordOptions([]);
                setKeywordLoadError(true);
              }
            } catch (parseErr) {
              console.error('キーワードデータのパースエラー:', parseErr);
              setKeywordOptions([]);
              setKeywordLoadError(true);
            }
          } else {
            setKeywordOptions([]);
            setKeywordLoadError(true);
          }
        }
      } catch (e) {
        console.error('キーワード取得エラー:', e);
        if (!cancelled) {
          setKeywordOptions([]);
          setKeywordLoadError(true);
        }
      }

      try {
        // 拡張フィールド
        const optionFieldResult = await optionFieldAPI.getOptionFieldList();
        if (!cancelled && optionFieldResult.statuscode === 200) {
          setOptionFields(optionFieldResult);
        }
      } catch (e) {
        console.error('拡張フィールド取得エラー:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ====== 備考にキーワード挿入 ======
  const insertKeyword = (textToInsert: string) => {
    const currentValue: string = form.getFieldValue(remarksFieldName) || '';
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    const start = textarea?.selectionStart ?? currentValue.length;
    const end = textarea?.selectionEnd ?? currentValue.length;
    const newValue = currentValue.substring(0, start) + textToInsert + currentValue.substring(end);
    form.setFieldsValue({ [remarksFieldName]: newValue });
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const pos = start + textToInsert.length;
        textarea.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  // 拡張フィールド変更
  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    const next = { ...optionFieldValues, [fieldKey]: value };
    onOptionFieldValuesChange(next);
  };

  // 備考の新規/既存チェックボックス。
  // existingRemarks が string のとき＝既存顧客（重複）あり。
  // 新規側と既存側で内容に差がある場合のみチェックボックスを表示する。
  const showRemarksToggle =
    typeof existingRemarks === 'string' &&
    typeof newRemarks === 'string' &&
    existingRemarks !== newRemarks;

  // 新規・既存どちらも反映しないのは不可（最後の1つはOFFにできない）
  const handleRemarksNewCheckedChange = (checked: boolean) => {
    if (!checked && !remarksExistingChecked) return; // 両方OFFは禁止
    onRemarksNewCheckedChange?.(checked);
  };
  const handleRemarksExistingCheckedChange = (checked: boolean) => {
    if (!checked && !remarksNewChecked) return; // 両方OFFは禁止
    onRemarksExistingCheckedChange?.(checked);
  };

  const REMARKS_TOGGLE_COLOR = '#0693e3';
  // ラベル（チェックボックス＋新規/既存）を上段、入力欄・値表示を下段にした縦並び構成
  const remarksRowStyle = (selected: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    backgroundColor: selected ? '#e6f4ff' : '#f9f9f9',
    border: `1px solid ${selected ? REMARKS_TOGGLE_COLOR : '#d9d9d9'}`,
    borderRadius: '4px',
  });

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        {/* ランク */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>ランク</Text>
          </div>
          <Form.Item name={rankFieldName} style={{ marginBottom: 0 }}>
            <Select placeholder="ランクを選択" allowClear>
              {rankOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* 備考 */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Text strong>備考</Text>
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<TagOutlined />}
                onClick={() => setKeywordModalVisible(true)}
              >
                キーワード入力
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<RobotOutlined />}
                onClick={assist.onAssistClick}
              >
                アシスト入力
              </Button>
            </Space>
          </div>

          {showRemarksToggle ? (
            // 新規・既存をチェックボックスで選択（両方/片方を反映）
            <div>
              {/* 1段目: 新規（仮登録）。チェックON時にその内容を反映。常に編集可 */}
              <div style={remarksRowStyle(remarksNewChecked)}>
                {/* 上段: チェックボックス＋ラベル */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <Checkbox
                    checked={remarksNewChecked}
                    onChange={(e) => handleRemarksNewCheckedChange(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  <Text style={{ fontWeight: remarksNewChecked ? 'bold' : 'normal' }}>新規</Text>
                </div>
                {/* 下段: 入力欄（全幅） */}
                <Form.Item name={remarksFieldName} style={{ margin: 0, width: '100%' }}>
                  <TextArea
                    placeholder="備考を入力"
                    autoSize={{ minRows: 3 }}
                    ref={textAreaRef}
                  />
                </Form.Item>
              </div>

              {/* 2段目: 既存（読み取り専用表示）。チェックON時にその内容を反映 */}
              <div style={{ ...remarksRowStyle(remarksExistingChecked), marginTop: 8 }}>
                {/* 上段: チェックボックス＋ラベル */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <Checkbox
                    checked={remarksExistingChecked}
                    onChange={(e) => handleRemarksExistingCheckedChange(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  <Text style={{ fontWeight: remarksExistingChecked ? 'bold' : 'normal' }}>既存</Text>
                </div>
                {/* 下段: 値表示（全幅） */}
                <div
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    whiteSpace: 'pre-wrap',
                    color: 'rgba(0, 0, 0, 0.65)',
                  }}
                >
                  {existingRemarks || '-'}
                </div>
              </div>
            </div>
          ) : (
            <Form.Item name={remarksFieldName} style={{ marginBottom: 0 }}>
              <TextArea placeholder="備考を入力" autoSize={{ minRows: 3 }} ref={textAreaRef} />
            </Form.Item>
          )}

          {/* 「顧客備考」欄の重複整備（doMergeRemarks） */}
          {showMergeRemarks && (
            <div style={{ marginTop: 16 }}>
              <Checkbox
                checked={mergeRemarksChecked}
                onChange={(e) => onMergeRemarksChange?.(e.target.checked)}
              >
                「顧客備考」欄の重複整備を行う
              </Checkbox>
              <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                重複した複数のキーワード（【】付きのキーワード）を一つに統合できます。
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* 拡張フィールド */}
      {optionFields?.customeroptionfieldlist && optionFields.customeroptionfieldlist.length > 0 && (
        <div className="option-field-group" style={{ marginBottom: 16 }}>
          <OptionFieldGroup
            title=""
            fields={optionFields.customeroptionfieldlist.filter(
              (field) => field.optionfieldkey !== 'furigana'
            )}
            values={optionFieldValues}
            onFieldChange={handleOptionFieldChange}
            prefix="customer"
          />
        </div>
      )}

      {/* キーワード入力モーダル */}
      <KeywordSelectModal
        open={keywordModalVisible}
        options={keywordOptions}
        onClose={() => setKeywordModalVisible(false)}
        onSelect={insertKeyword}
        error={keywordLoadError}
        destroyOnClose
        maskClosable={false}
      />

      {/* アシスト入力モーダル（描画・初期化・挿入は useAssistInput が担当） */}
      <AssistInputModal
        {...assist.assistModalProps}
        width={600}
        bodyStyle={{ overflow: 'hidden', padding: '2px' }}
      />
    </>
  );
};

export default AdditionalInfoSection;
